import React, { ReactNode, useRef, useState } from "react";
import { Box, Button, Chip, Divider, FormControl, FormControlLabel, FormLabel, Grid, IconButton, Link, Paper, Radio, RadioGroup, Typography } from "@mui/material";
import { Upload, Check, Close, Business, Description } from "@mui/icons-material";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import { AxiosError } from "axios";

import { BaseService } from "../../services/base-service";
import { URLS } from "../../services/app-urls";
import { translate } from "../../translate/translate";
import { Customer } from "../../models/general/customer";
import { ImportType } from "../../models/general/import";
import { ForecastType, FORECAST_TYPE_LABELS } from "../../models/general/forecast";

import Loading from "../loading/loading";
import AutocompleteSimple from "../autocomplete/autocomplete";
import ImportHistoryComponent from "./import-history.component";
import { hasMultipleImportFileErrors, MultipleImportFileErrorResponse } from "../../utils/error-utils";
import { ErrorComponent } from "../error/error.component";
import { StatusCodes } from "http-status-codes";

interface ImportFileProps {
    type: "forecast" | "bom" | "stock_and_po";
    /** Oculta a seção de upload, mantendo apenas o histórico */
    hideUpload?: boolean;
    /** Trigger externo para forçar reload do histórico */
    reloadTrigger?: number;
    /** Callback chamado assim que o upload é disparado */
    onUploadStart?: () => void;
    /** Callback chamado após upload bem-sucedido */
    onUploadSuccess?: () => void;
    /** Callback chamado quando o upload falha */
    onUploadError?: () => void;
    children?: ReactNode;
    /** Props opcionais para customizar a exibição do histórico (colunas etc) */
    historyProps?: any;
}

const ImportFile: React.FC<ImportFileProps> = ({
    type,
    hideUpload = false,
    reloadTrigger = 0,
    onUploadStart,
    onUploadSuccess,
    onUploadError,
    children,
    historyProps,
}) => {
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [customer, setCustomerValue] = useState<Customer | null>(null);
    const [forecastType, setForecastType] = useState<ForecastType | null>(null);
    const [customerCount, setCustomerCount] = useState<number | null>(null);
    const [reloadHistory, setReloadHistory] = useState(0);
    const [clearAutocomplete, setClearAutocomplete] = useState(false);

    React.useEffect(() => {
        if (reloadTrigger > 0) {
            setReloadHistory(prev => prev + 1);
        }
    }, [reloadTrigger]);
    const [resetAutocomplete] = useState(false);

    React.useEffect(() => {
        if (type === "forecast" && selectedFiles) {
            serviceCustomer.clearParameters();
            serviceCustomer.addParameter("active", true);
            serviceCustomer.addParameter("limit", 1);
            serviceCustomer.addParameter("offset", 0);
            serviceCustomer.getAllPaginate()
                .then((response) => {
                    const total = response.data.count ?? response.data.results?.length ?? 0;
                    setCustomerCount(total);
                    const results = response.data.results || [];
                    if (total === 1 && results.length === 1) {
                        setCustomerValue(results[0]);
                        setClearAutocomplete(false);
                    } else {
                        setCustomerValue(null);
                    }
                })
                .catch(() => setCustomerCount(0));
        }
    }, [selectedFiles, type]);

    const serviceCustomer = new BaseService<Customer>(URLS.CUSTOMER);
    const serviceDownloadTemplate = new BaseService<Blob>(URLS.DOWNLOAD_TEMPLATE);
    const serviceImport = {
        forecast: new BaseService(URLS.IMPORT_FORECAST),
        bom: new BaseService(URLS.IMPORT_BOM),
        stock_and_po: new BaseService(URLS.IMPORT_STOCK_AND_PO),
    };

    const acceptConfig: Record<string, string[]> = {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        "application/vnd.ms-excel": [".xls"],
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const validateAndSetFile = (file: File[]): void => {
        const isAccepted = Object.keys(acceptConfig).some((mimeType) =>
            acceptConfig[mimeType].includes(`.${file[0].name.split(".").pop() || ""}`)
        );

        if (isAccepted) {
            setSelectedFiles(file);
        } else {
            toast.error(translate.t("import_file_type_not_accepted"), {
                autoClose: false,
            });
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            validateAndSetFile(Array.from(event.target.files));
        }
    };

    const handleSelectCustomer = (customer: Customer) => {
        setCustomerValue(customer);
        setClearAutocomplete(false);
    };

    const handleDownloadTemplate = async () => {
        try {
            serviceDownloadTemplate.clearParameters();
            serviceDownloadTemplate.addParameter("type", type);
            const response = await serviceDownloadTemplate.getTemplate();

            const blob = new Blob([response.data], { type: response.headers["content-type"] as string });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", `${type}_template.xlsx`);
            document.body.appendChild(link);
            link.click();

            link.parentNode?.removeChild(link);
        } catch (error) {
            const axiosError = error as AxiosError;
            ErrorComponent(axiosError, false);
        }
    };

    const handleClear = (resetForm?: () => void) => {
        setSelectedFiles(null);
        setClearAutocomplete(true);
        setCustomerValue(null);
        setForecastType(null);
        setCustomerCount(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        resetForm && resetForm();
    };

    const handleMultipleImportFileErrors = (error: AxiosError<MultipleImportFileErrorResponse>) => {
        const { errors } = (error?.response?.data as MultipleImportFileErrorResponse) || {};
        const totalFiles = selectedFiles?.length || 0;

        if (totalFiles === 1) {
            ErrorComponent(new AxiosError(errors?.[0]?.error || "", error.code), false);
        } else if (errors?.length === totalFiles) {
            ErrorComponent(new AxiosError(
                translate.t("all_files_failed_to_import"), error.code),
            false
            );
        } else {
            toast.warning(
                translate.t("files_failed_to_import", {
                    failed: errors?.length,
                    total: totalFiles,
                }));
        }
    };

    const handleImportExceptions = (error: AxiosError) => {
        if (hasMultipleImportFileErrors(error)) {
            handleMultipleImportFileErrors(error as AxiosError<MultipleImportFileErrorResponse>);
        } else if (type === "bom" || type === "stock_and_po") {
            toast.error(translate.t("failed_to_upload_file"), {
                autoClose: false,
            });
        } else {
            ErrorComponent(error, false);
        }
    };

    const handleSubmit = () => {
        if (!selectedFiles) {
            toast.error(translate.t("file_must_be_selected"), {
                autoClose: false,
            });
            return;
        }

        const shouldShowBlockingLoading = type === "forecast";
        setIsLoading(shouldShowBlockingLoading);
        const service = serviceImport[type];
        service.clearParameters();

        const payload = type === "forecast" ? {
            customer_id: customer?.id,
            type: forecastType,
        } : null;

        onUploadStart?.();

        service.importFile(payload, selectedFiles)
            .then((response) => {
                if (response.status === StatusCodes.PARTIAL_CONTENT) {
                    handleImportExceptions({ response } as AxiosError);
                    onUploadError?.();
                    return;
                }

                if (type !== "bom") {
                    toast.success(translate.t("file_uploaded_successfully", { count: selectedFiles.length || 1 }));
                }

                // Notifica que upload foi bem-sucedido
                onUploadSuccess?.();
            })
            .catch((error) => {
                handleImportExceptions(error as AxiosError);
                onUploadError?.();
            })
            .finally(() => {
                handleClear();
                setIsLoading(false);
                setReloadHistory(prev => prev + 1);
            });
    };

    const getImportType = (type: string) => {
        if (type === "forecast") {
            return ImportType.FORECAST;
        }
        if (type === "bom") {
            return ImportType.BOM;
        }
        return ImportType.STOCK_PURCHASE_ORDER;
    };

    return (
        <>
            <Loading open={isLoading} />
            <Paper
                variant="outlined"
                sx={type === "forecast" ? {
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
                } : undefined}
            >
                {children}
                {!hideUpload && <Box p={type === "forecast" ? 2 : 3}>
                    <Formik
                        initialValues={{
                            customer: "",
                        }}
                        onSubmit={handleSubmit}
                    >
                        {({ resetForm }) => (
                            <Form>
                                <Grid container spacing={2}  marginBottom={2}>
                                    <Grid item xs={12} sm={6} display="flex" alignItems="center" gap={2}>
                                        <Button
                                            component="label"
                                            role={undefined}
                                            variant="outlined"
                                            tabIndex={-1}
                                            startIcon={<Upload />}
                                            color="primary"
                                            className="button-new"
                                        >
                                            {translate.t("import")}
                                            <input
                                                ref={fileInputRef}
                                                multiple={false}
                                                type="file"
                                                style={{
                                                    clip: "rect(0 0 0 0)",
                                                    clipPath: "inset(50%)",
                                                    height: 1,
                                                    overflow: "hidden",
                                                    position: "absolute",
                                                    bottom: 0,
                                                    left: 0,
                                                    whiteSpace: "nowrap",
                                                    width: 1,
                                                }}
                                                onChange={handleFileChange}
                                            />
                                        </Button>
                                        <Grid item xs={12} sm={6} display="flex" gap={2}>
                                            <Button
                                                type="submit"
                                                startIcon={<Check />}
                                                variant="contained"
                                                color="primary"
                                                className="button-new"
                                                disabled={!selectedFiles || (type === "forecast" && (!forecastType || !customer))}
                                            >
                                                {translate.t("send")}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid container display="flex" flexDirection="column" spacing={2}>
                                    <Grid item gap={2}>
                                        <Box
                                            border="1px solid #CFD8DC"
                                            borderRadius={1}
                                            overflow="hidden"
                                        >
                                            {selectedFiles && selectedFiles.length > 0 && (
                                                <>
                                                    <Grid
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        height={type === "forecast" ? "auto" : 56}
                                                        maxHeight={type === "forecast" ? "none" : 56}
                                                        px={2}
                                                        py={type === "forecast" ? 1 : 2}
                                                        gap={2}
                                                        flexWrap={{ xs: "wrap", md: "nowrap" }}
                                                        sx={type === "forecast" ? { backgroundColor: "#EEF2FF" } : undefined}
                                                    >
                                                        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                                                            {type === "forecast" && <Description color="primary" fontSize="small" />}
                                                            {selectedFiles.length === 1 ? (
                                                                <Typography variant="body2" noWrap>
                                                                    {translate.t("file_selected")}: {" "}
                                                                    <Box component="span" fontWeight={500}>{selectedFiles[0].name}</Box>
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="body2">
                                                                    {selectedFiles.length} {translate.t("files_selected")}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        {type === "forecast" && (
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent={{ xs: "flex-start", md: "flex-end" }}
                                                                gap={1}
                                                                ml={{ xs: 0, md: "auto" }}
                                                                flexGrow={{ xs: 1, md: 0 }}
                                                            >
                                                                {customerCount === 1 && customer ? (
                                                                    <>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {translate.t("customer")}:
                                                                        </Typography>
                                                                        <Chip
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="primary"
                                                                            icon={<Business fontSize="small" />}
                                                                            label={customer.name}
                                                                            sx={{ backgroundColor: "background.paper" }}
                                                                        />
                                                                    </>
                                                                ) : customerCount === 0 ? (
                                                                    <Typography variant="body2" color="error">
                                                                        {translate.t("forecast_customer_required")}
                                                                    </Typography>
                                                                ) : customerCount !== null ? (
                                                                    <Box width={{ xs: "100%", sm: 320 }}>
                                                                        <AutocompleteSimple
                                                                            service={serviceCustomer}
                                                                            label={translate.t("select_customer")}
                                                                            searchField="name"
                                                                            display="name"
                                                                            onSelectElement={handleSelectCustomer}
                                                                            element={customer}
                                                                            clear={clearAutocomplete}
                                                                            reset={resetAutocomplete}
                                                                            filters={[{ active: true }]}
                                                                            required={true}
                                                                            fullWidth
                                                                            size="small"
                                                                        />
                                                                    </Box>
                                                                ) : null}
                                                            </Box>
                                                        )}
                                                        <IconButton
                                                            sx={{
                                                                padding: 0,
                                                                color: type === "forecast" ? "text.secondary" : "primary.main",
                                                            }}
                                                            onClick={() => handleClear(resetForm)}
                                                            aria-label={translate.t("clear")}
                                                        >
                                                            <Close fontSize="inherit" />
                                                        </IconButton>
                                                    </Grid>
                                                    <Divider />
                                                    {type === "forecast" && (
                                                        <Box px={2} py={1.5}>
                                                            <FormControl required fullWidth>
                                                                <FormLabel id="forecast-type-label" sx={{ mb: 1 }}>
                                                                    {translate.t("forecast_type")}
                                                                </FormLabel>
                                                                <RadioGroup
                                                                    aria-labelledby="forecast-type-label"
                                                                    value={forecastType ?? ""}
                                                                    onChange={(event) => setForecastType(event.target.value as ForecastType)}
                                                                    sx={{
                                                                        display: "grid",
                                                                        gridTemplateColumns: {
                                                                            xs: "1fr",
                                                                            sm: "repeat(2, minmax(280px, 440px))",
                                                                        },
                                                                        gap: 1,
                                                                        justifyContent: "flex-start",
                                                                    }}
                                                                >
                                                                    {Object.values(ForecastType).map((value) => {
                                                                        const selected = forecastType === value;
                                                                        return (
                                                                            <FormControlLabel
                                                                                key={value}
                                                                                value={value}
                                                                                control={<Radio size="small" />}
                                                                                label={(
                                                                                    <Box py={0.5}>
                                                                                        <Typography variant="body2" fontWeight={600}>
                                                                                            {FORECAST_TYPE_LABELS[value]}
                                                                                        </Typography>
                                                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                                                            {translate.t(value === ForecastType.NET
                                                                                                ? "forecast_net_description"
                                                                                                : "forecast_flex_dsi_description")}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                )}
                                                                                sx={{
                                                                                    alignItems: "flex-start",
                                                                                    border: "1px solid",
                                                                                    borderColor: selected ? "primary.main" : "#CFD8DC",
                                                                                    borderRadius: 1,
                                                                                    backgroundColor: selected ? "#EEF2FF" : "background.paper",
                                                                                    boxShadow: selected ? "0 0 0 1px var(--primary-main)" : "none",
                                                                                    m: 0,
                                                                                    px: 1,
                                                                                    py: 0.5,
                                                                                    minHeight: 66,
                                                                                    "&:hover": { borderColor: "primary.main", backgroundColor: "#F8FAFF" },
                                                                                }}
                                                                            />
                                                                        );
                                                                    })}
                                                                </RadioGroup>
                                                            </FormControl>
                                                        </Box>
                                                    )}
                                                    {type === "forecast" && <Divider />}
                                                </>
                                            )}
                                            <Grid
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                height={type === "forecast" ? "auto" : 72}
                                                minHeight={type === "forecast" ? 52 : 72}
                                                maxHeight={type === "forecast" ? "none" : 72}
                                                px={2}
                                                py={type === "forecast" ? 1.25 : 2}
                                            >
                                                <Box>
                                                    <Typography variant="body2">
                                                        {translate.t("file_must_follow_template")}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {translate.t("download_template.text")}
                                                        <Link
                                                            component="button"
                                                            type="button"
                                                            underline="none"
                                                            onClick={handleDownloadTemplate}
                                                        >
                                                            {translate.t("download_template.link")}
                                                        </Link>
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </Box>}
                <ImportHistoryComponent key={reloadHistory} import_type={getImportType(type)} {...historyProps} />
            </Paper>
        </>
    );
};

export default ImportFile;
